import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { API_BASE_URL, API_BASE_URL_UI } from "../../environment";
import { getAuthHeaders } from "../../utils/authUtils";
import { AuthContext } from "../../context/authcontext";

interface LocationState {
  email?: string;
}

const SocialConnection = () => {
  const route = all_routes;
  const [loadingState, setLoadingState] = useState<
    "verifying" | "success" | "error" | "redirecting"
  >("verifying");
  const [message, setMessage] = useState("We're verifying your LINE connection");
  const { selectedTenantId, auth } = useContext(AuthContext);

  const location = useLocation();
  const state = (location.state as LocationState) ?? {};
  const email = state.email ?? "";

  useEffect(() => {
    const verifySocialConnection = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const state = urlParams.get("state");
        const code = urlParams.get("code");

        if (!state || !code) {
          setLoadingState("error");
          setMessage("Missing verification parameters");
          return;
        }

        const user_id = auth?.user_id;
        if (!user_id) {
          setLoadingState("error");
          setMessage("User ID not found. Please login again.");
          return;
        }

        const headers = getAuthHeaders();
        headers["Content-Type"] = "application/json";
        
        const response = await fetch(
          `${API_BASE_URL}/social/line/exchange-token/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              state,
              code,
              tenant_id: selectedTenantId,
              user_id: user_id,
            }),
          }
        );

        const data = await response.json();

        if (data.success) {
          setLoadingState("success");
          setMessage("LINE connection verified successfully!");
        } else {
          setLoadingState("error");
          setMessage("LINE verification failed. Please try again.");
        }

        setTimeout(() => {
          setLoadingState("redirecting");
          setMessage("Redirecting you...");
        }, 2000);

        setTimeout(() => {
          window.location.href = `${API_BASE_URL_UI}/integration-settings/email-settings`;
        }, 4000);
      } catch (error) {
        console.error("LINE verification error:", error);
        setLoadingState("error");
        setMessage("An error occurred during verification. Please try again.");

        setTimeout(() => {
          setLoadingState("redirecting");
          setMessage("Redirecting you...");
        }, 2000);

        setTimeout(() => {
          window.location.href = `${API_BASE_URL_UI}/integration-settings/email-settings`;
        }, 4000);
      }
    };

    verifySocialConnection();
  }, [location.search, auth?.user_id, selectedTenantId]);

  const getStatusColor = () => {
    switch (loadingState) {
      case "success":
        return "text-success";
      case "error":
        return "text-danger";
      default:
        return "text-primary";
    }
  };

  const getIcon = () => {
    switch (loadingState) {
      case "success":
        return (
          <div className="text-center mb-4">
            <div
              className="d-inline-flex align-items-center justify-content-center bg-success-subtle text-success rounded-circle"
              style={{ width: "4rem", height: "4rem" }}
            >
              <i className="fas fa-check" style={{ fontSize: "2rem" }}></i>
            </div>
          </div>
        );
      case "error":
        return (
          <div className="text-center mb-4">
            <div
              className="d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger rounded-circle"
              style={{ width: "4rem", height: "4rem" }}
            >
              <i className="fas fa-times" style={{ fontSize: "2rem" }}></i>
            </div>
          </div>
        );
      default:
        return (
          <div className="text-center mb-4">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="account-content">
      <div
        className="d-flex flex-wrap w-100 vh-100 overflow-hidden"
        style={{ backgroundColor: "white" }}
      >
        <div className="d-flex align-items-center justify-content-center flex-wrap vh-100 overflow-auto p-4 w-100">
          <div className="mx-auto mw-450 text-center">
            <div className="text-center mb-5">
              <ImageWithBasePath
                src="assets/img/Logo.png"
                className="img-fluid"
                alt="Logo"
              />
            </div>

            {getIcon()}

            <div className="text-center mb-4">
              <h4 className={`mb-2 fs-20 ${getStatusColor()}`}>{message}</h4>
              <p className="mb-3 text-muted">
                Please wait while we complete the LINE connection process.
              </p>
            </div>

            <div className="text-center mt-5">
              <p className="fw-medium text-gray">Copyright © 2025 - CRMS</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialConnection;
